import { Customer, Dealer, LicenseBundle, LicenseType, licenseTypeCode, Module } from '@app/models';
import { isBefore, subDays } from 'date-fns';
import { AppMode } from '../appMode.model';
import { Brand } from './Brand.model';

class ModuleActivationResponse {
  code: string;
  name: string;
  brandName: string;
  crossDevice: number;

  constructor(params: any = {}) {
    this.code = params.neowiseCode;
    this.name = params.name;
    this.brandName = params.brandName;
    this.crossDevice = params.hwModuleCrossDevice;
  }
}

export class License {
  licenseId: string;
  licenseBundleId: string;
  licenseBundle: LicenseBundle;
  dealerId: string;
  dealer: Dealer;
  orderNumber: string;
  moduleId: string;
  module: Module;
  hwModule?: ModuleActivationResponse;
  swModule?: ModuleActivationResponse;
  creationUserId: string;
  creationTime: Date;
  licenseKey: string;
  offlineActivation: number;
  offlineLicenseKey: string;
  licenseTypeId: string;
  licenseType: LicenseType;
  licenseDurationTime: number;
  licenseDurationUnits: number;
  activationEnabledFrom: Date;
  activationDisabled: number;
  deleted: number;
  customerId: string;
  customer: Customer;
  activationTime: Date;
  expirationTime: Date;
  customerStudyId: string;
  offlineLicenseKeyExpirationTime: Date;
  maxWorkstations: number;
  hwSerialNumber: string;
  usedUnits: number;
  usedUnitsLastUpdateTime: Date;
  appMode: AppMode;
  brand: Brand;

  get productName() {
    return this.brandName ?? this.module?.name;
  }

  get brandName() {
    return this.module?.moduleBrandNames?.[0]?.name ?? this.hwModule?.brandName;
  }

  get expired() {
    return this.expirationTime && isBefore(this.expirationTime, new Date());
  }

  get validFrom() {
    if (this.licenseType?.code != licenseTypeCode.TIME) return this.activationTime;

    if (this.expirationTime) return subDays(this.expirationTime, this.licenseDurationTime);

    return null;
  }

  constructor(params: LicenseParams) {
    this.licenseId = params.licenseId;
    this.licenseBundleId = params.licenseBundleId;
    this.dealerId = params.dealerId;
    this.orderNumber = params.orderNumber;
    this.moduleId = params.moduleId;
    this.creationUserId = params.creationUserId;
    this.creationTime = params.creationTime;
    this.licenseKey = params.licenseKey;
    this.offlineActivation = params.offlineActivation;
    this.offlineLicenseKey = params.offlineLicenseKey;
    this.licenseTypeId = params.licenseTypeId;
    this.licenseDurationTime = params.licenseDurationTime;
    this.licenseDurationUnits = params.licenseDurationUnits;
    this.activationEnabledFrom = params.activationEnabledFrom;
    this.activationDisabled = params.activationDisabled;
    this.deleted = params.deleted;
    this.customerId = params.customerId;
    this.activationTime = params.activationTime && new Date(params.activationTime);
    this.expirationTime = params.expirationTime && new Date(params.expirationTime);
    this.customerStudyId = params.customerStudyId;
    this.module = params.module && new Module(params.module);
    this.licenseType = params.licenseType && new LicenseType(params.licenseType);
    this.licenseBundle = params.licenseBundle && new LicenseBundle(params.licenseBundle);
    this.dealer = params.dealer && new Dealer(params.dealer);
    this.customer = params.customer;
    this.offlineLicenseKeyExpirationTime = params.offlineLicenseKeyExpirationTime && new Date(params.offlineLicenseKeyExpirationTime);
    this.maxWorkstations = params.maxWorkstations;
    this.hwSerialNumber = params.hwSerialNumber;
    this.usedUnits = params.usedUnits;
    this.usedUnitsLastUpdateTime = params.usedUnitsLastUpdateTime;
    this.appMode = params.appMode && new AppMode(params.appMode);
    this.brand = params.brand && new Brand(params.brand);
    this.hwModule = params.hwModule && new ModuleActivationResponse(params.hwModule);
    this.swModule = params.swModule && new ModuleActivationResponse(params.swModule);
  }
}

export interface LicenseParams {
  licenseId?: string;
  licenseBundleId?: string;
  licenseBundle?: LicenseBundle;
  dealerId?: string;
  dealer?: Dealer;
  orderNumber?: string;
  moduleId?: string;
  module: Module;
  creationUserId?: string;
  creationTime?: Date;
  licenseKey?: string;
  offlineActivation?: number;
  offlineLicenseKey?: string;
  licenseTypeId?: string;
  licenseDurationTime?: number;
  licenseDurationUnits?: number;
  activationEnabledFrom?: Date;
  activationDisabled?: number;
  deleted?: number;
  customerId?: string;
  customer?: Customer;
  activationTime?: Date;
  expirationTime?: Date;
  customerStudyId?: string;
  licenseType: LicenseType;
  offlineLicenseKeyExpirationTime?: Date;
  maxWorkstations?: number;
  hwSerialNumber?: string;
  usedUnits?: number;
  usedUnitsLastUpdateTime?: Date;
  appMode?: AppMode;
  brand?: Brand;
  hwModule?: ModuleActivationResponse;
  swModule?: ModuleActivationResponse;
}
